import { useState, useRef, useImperativeHandle, forwardRef, useCallback, useEffect, useLayoutEffect } from 'react'
import { Send, Square, Image, ChevronDown, Infinity as InfinityIcon, X, FolderOpen, SlidersHorizontal } from 'lucide-react'
import { MicButton, MicButtonHandle } from '@/shared/components/MicButton'
import { VoiceChatButton } from '@/shared/components/VoiceChatButton'
import { SlashCommandMenu, type SlashCommand } from './SlashCommandMenu'
import type { AgentDefinition } from '@/shared/lib/api'
import type { Workspace } from '@/features/orchestration/types'
import { getAuthHeader } from '@/shared/lib/auth'
import { API_URL } from '@/config'
import { useIsMobile } from '@/shared/hooks/useIsMobile'

export interface AttachmentFile {
  id: string           // unique id for React key
  name: string
  mediaType: string
  base64Data: string   // base64-encoded (no data URI prefix)
  previewUrl: string   // for images: object URL or data URI for preview
  isImage: boolean
  size: number
}

interface ChatInputProps {
  onSend: (text: string, attachments: AttachmentFile[]) => void
  onStop?: () => void
  disabled?: boolean
  model: string
  onModelChange: (model: string) => void
  agentDefinitionId: number | null
  onAgentChange: (id: number | null) => void
  agentDefinitions: AgentDefinition[]
  /** When true, the agent selector is disabled (conversation has messages) */
  isAgentLocked?: boolean
  workspaceId: number | null
  onWorkspaceChange: (id: number | null) => void
  workspaces: Workspace[]
  micButtonRef?: React.RefObject<MicButtonHandle>
  voiceChat?: {
    isActive: boolean
    isListening: boolean
    isSpeaking: boolean
    isTranscribing: boolean
    onToggle: () => void
  }
}

export interface ChatInputHandle {
  focus: () => void
}

const MODEL_LABELS: Record<string, string> = {
  'claude-haiku-4-5-20251001': 'Haiku',
  'claude-sonnet-4-6': 'Sonnet 4.6',
  'claude-opus-4-6': 'Opus 4.6',
}


export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(
  function ChatInput(
    { onSend, onStop, disabled, model, onModelChange, agentDefinitionId, onAgentChange, agentDefinitions, isAgentLocked = false, workspaceId, onWorkspaceChange, workspaces, micButtonRef, voiceChat },
    ref
  ) {
    const [input, setInput] = useState('')
    const [attachments, setAttachments] = useState<AttachmentFile[]>([])
    const [slashFilter, setSlashFilter] = useState<string | null>(null)
    const [activeCommandIndex, setActiveCommandIndex] = useState(0)
    const [slashCommands, setSlashCommands] = useState<SlashCommand[]>([])
    const [menuAnchor, setMenuAnchor] = useState<{ top: number; left: number; width: number } | null>(null)
    const [showSettingsPanel, setShowSettingsPanel] = useState(false)
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    // Use 768px (Tailwind's md breakpoint) to avoid selector overflow on narrow viewports like iPad.
    // Below 768px, the settings button + bottom sheet provides vertical layout for selectors.
    const isMobile = useIsMobile(768)

    useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus()
    }))

    // Update menu anchor position when the slash menu opens or window resizes
    useLayoutEffect(() => {
      if (slashFilter === null) {
        setMenuAnchor(null)
        return
      }
      const updateAnchor = () => {
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect()
          setMenuAnchor({ top: rect.top, left: rect.left, width: rect.width })
        }
      }
      updateAnchor()
      window.addEventListener('resize', updateAnchor)
      window.addEventListener('scroll', updateAnchor, true)
      return () => {
        window.removeEventListener('resize', updateAnchor)
        window.removeEventListener('scroll', updateAnchor, true)
      }
    }, [slashFilter])

    // Fetch slash commands dynamically from the backend on mount
    useEffect(() => {
      fetch(`${API_URL}/api/slash-commands`, { headers: getAuthHeader() })
        .then((res) => {
          if (!res.ok) {
            console.error(`[SlashCommands] Failed to load: ${res.status} ${res.statusText}`)
            return []
          }
          return res.json()
        })
        .then((data: SlashCommand[]) => setSlashCommands(data))
        .catch((err) => console.error('[SlashCommands] Fetch error:', err))
    }, [])

    // Detect if cursor is in a slash command context
    const getSlashFilter = (text: string, cursorPos: number): string | null => {
      const textBeforeCursor = text.slice(0, cursorPos)
      const lastWordMatch = textBeforeCursor.match(/(?:^|\s)(\/\S*)$/)
      if (!lastWordMatch) return null
      return lastWordMatch[1].slice(1) // return text after "/"
    }

    const handleSend = () => {
      if ((!input.trim() && attachments.length === 0) || disabled) return
      onSend(input, attachments)
      setInput('')
      setAttachments([])
      setSlashFilter(null)
      setShowSettingsPanel(false) // Close settings panel on send
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Escape closes settings panel
      if (e.key === 'Escape' && showSettingsPanel) {
        e.preventDefault()
        setShowSettingsPanel(false)
        return
      }

      // Slash command menu is open
      if (slashFilter !== null) {
        const filteredCommands = slashCommands.filter((cmd) =>
          cmd.name.toLowerCase().startsWith(slashFilter.toLowerCase())
        )

        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setActiveCommandIndex((prev) => (prev + 1) % filteredCommands.length)
          return
        }

        if (e.key === 'ArrowUp') {
          e.preventDefault()
          setActiveCommandIndex((prev) =>
            prev === 0 ? filteredCommands.length - 1 : prev - 1
          )
          return
        }

        if (e.key === 'Enter' && !e.shiftKey && filteredCommands.length > 0) {
          e.preventDefault()
          handleCommandSelect(filteredCommands[activeCommandIndex])
          return
        }

        if (e.key === 'Escape') {
          e.preventDefault()
          setSlashFilter(null)
          setActiveCommandIndex(0)
          return
        }
      }

      // Normal input handling
      if (e.key === 'Enter' && !e.shiftKey && !disabled) {
        e.preventDefault()
        handleSend()
      }
    }

    // Auto-resize function - can be called from onChange, paste, or transcript handlers
    const autoResize = useCallback((el: HTMLTextAreaElement) => {
      el.style.height = 'auto'
      el.style.height = `${Math.min(el.scrollHeight, 200)}px`
    }, [])

    const handleCommandSelect = useCallback((command: SlashCommand) => {
      const cursorPos = textareaRef.current?.selectionStart ?? input.length
      const textBeforeCursor = input.slice(0, cursorPos)

      // Replace the "/filter" portion with "/command-name "
      const replaced = textBeforeCursor.replace(/(?:^|\s)(\/\S*)$/, (match, cmd) => {
        return match.slice(0, match.length - cmd.length) + `/${command.name} `
      })

      const newText = replaced + input.slice(cursorPos)
      setInput(newText)
      setSlashFilter(null)
      setActiveCommandIndex(0)

      // Re-focus and move cursor to end of inserted command
      requestAnimationFrame(() => {
        if (textareaRef.current) {
          const pos = replaced.length
          textareaRef.current.focus()
          textareaRef.current.setSelectionRange(pos, pos)
          // Also trigger resize
          autoResize(textareaRef.current)
        }
      })
    }, [input, autoResize])

    const handleTranscript = useCallback((text: string) => {
      setInput((prev) => {
        const next = prev ? `${prev} ${text}` : text
        requestAnimationFrame(() => {
          if (textareaRef.current) {
            textareaRef.current.value = next
            autoResize(textareaRef.current)
          }
        })
        return next
      })
    }, [autoResize])

    const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const items = Array.from(e.clipboardData.items)
      const imageItems = items.filter(item => item.type.startsWith('image/'))
      if (imageItems.length === 0) return

      e.preventDefault() // prevent pasting as text

      imageItems.forEach(item => {
        const blob = item.getAsFile()
        if (!blob) return

        const reader = new FileReader()
        reader.onload = (event) => {
          const dataUrl = event.target?.result as string
          const base64Data = dataUrl.split(',')[1]
          const mediaType = blob.type || 'image/png'
          const name = `pasted-image-${Date.now()}.png`

          const newAttachment: AttachmentFile = {
            id: Math.random().toString(36).slice(2),
            name,
            mediaType,
            base64Data,
            previewUrl: dataUrl,
            isImage: true,
            size: blob.size,
          }
          setAttachments(prev => [...prev, newAttachment])
        }
        reader.readAsDataURL(blob)
      })
    }, [])

    const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || [])
      files.forEach(file => {
        const reader = new FileReader()
        reader.onload = (event) => {
          const dataUrl = event.target?.result as string
          const base64Data = dataUrl.split(',')[1]
          const isImage = file.type.startsWith('image/')

          setAttachments(prev => [...prev, {
            id: Math.random().toString(36).slice(2),
            name: file.name,
            mediaType: file.type,
            base64Data,
            previewUrl: isImage ? dataUrl : '',
            isImage,
            size: file.size,
          }])
        }
        reader.readAsDataURL(file)
      })
      // Reset file input so same file can be reselected
      e.target.value = ''
    }, [])

    const removeAttachment = useCallback((id: string) => {
      setAttachments(prev => prev.filter(a => a.id !== id))
    }, [])

    return (
      <div className="relative" ref={containerRef}>
        {/* Slash command menu — rendered via portal to escape overflow:hidden parents */}
        {slashFilter !== null && menuAnchor !== null && (
          <SlashCommandMenu
            commands={slashCommands}
            filter={slashFilter}
            onSelect={handleCommandSelect}
            activeIndex={activeCommandIndex}
            onActiveIndexChange={setActiveCommandIndex}
            anchor={menuAnchor}
          />
        )}

        <div className="flex flex-col rounded-xl border border-white/10 bg-white/[0.03] focus-within:border-white/20 transition-colors overflow-hidden">
        {/* Attachment preview strip */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 px-4 pt-3">
            {attachments.map((attachment) => (
              <div
                key={attachment.id}
                className="relative group flex items-center gap-2 bg-white/5 rounded-md border border-white/10 overflow-hidden"
              >
                {attachment.isImage ? (
                  <div className="relative w-12 h-12 flex-shrink-0">
                    <img
                      src={attachment.previewUrl}
                      alt={attachment.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="px-3 py-2 text-xs text-white/60">
                    {attachment.name}
                  </div>
                )}
                <button
                  onClick={() => removeAttachment(attachment.id)}
                  className="absolute top-0.5 right-0.5 p-0.5 bg-black/60 hover:bg-black/80 rounded text-white/70 hover:text-white transition-colors"
                  title="Remove attachment"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Textarea — transparent, no chrome */}
        <textarea
          ref={textareaRef}
          rows={1}
          className="w-full px-4 pt-3 pb-2 bg-transparent text-[#EEEEEE] placeholder-white/30 text-base resize-none outline-none leading-relaxed"
          placeholder="Plan, @ for context, / for commands"
          value={input}
          disabled={disabled}
          onChange={(e) => {
            const newValue = e.target.value
            setInput(newValue)

            // Detect slash command context
            const cursorPos = e.currentTarget.selectionStart ?? newValue.length
            const filter = getSlashFilter(newValue, cursorPos)
            setSlashFilter(filter)
            if (filter !== null) {
              setActiveCommandIndex(0)
            }

            // Auto-resize on change
            autoResize(e.currentTarget)
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          style={{ minHeight: '44px', maxHeight: '200px', overflowY: 'auto' }}
        />

        {/* Bottom toolbar - Two rows */}
        <div className="border-t border-white/5 pt-2 pb-2">
          {/* Top row: Action buttons with Send right-aligned */}
          <div className="flex items-center gap-2 px-3 pb-2">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf,.txt,.md,.csv,.json"
              multiple
              className="hidden"
              onChange={handleFileChange}
            />

            {/* Image button */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-2 max-sm:p-2.5 rounded-md text-white/40 hover:text-white/60 hover:bg-white/5 transition-colors flex-shrink-0"
              title="Attach file"
              aria-label="Attach file"
            >
              <Image size={18} className="max-sm:w-5 max-sm:h-5" />
            </button>

            {/* Mic */}
            <div className="flex-shrink-0">
              <MicButton ref={micButtonRef} onTranscript={handleTranscript} iconSize={20} ghost />
            </div>

            {/* Voice Chat */}
            {voiceChat && (
              <div className="flex-shrink-0">
                <VoiceChatButton
                  isActive={voiceChat.isActive}
                  isListening={voiceChat.isListening}
                  isSpeaking={voiceChat.isSpeaking}
                  isTranscribing={voiceChat.isTranscribing}
                  onToggle={voiceChat.onToggle}
                  iconSize={20}
                />
              </div>
            )}

            {/* Settings button - only visible on mobile (under 400px) */}
            {isMobile && (
              <button
                type="button"
                onClick={() => setShowSettingsPanel(!showSettingsPanel)}
                className="p-2.5 rounded-md text-white/40 hover:text-white/60 hover:bg-white/5 transition-colors flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center"
                title="Chat settings"
                aria-label="Chat settings"
              >
                <SlidersHorizontal size={18} className="w-5 h-5" />
              </button>
            )}

            {/* Local chip - hidden on mobile */}
            <button
              type="button"
              className="hidden sm:flex items-center gap-1 text-[10px] text-white/30 hover:text-white/50 transition-colors"
            >
              <span className="inline-block w-2.5 h-2.5 rounded-sm border border-white/20 bg-white/5" />
              <span>Local</span>
              <ChevronDown size={9} className="text-white/20" />
            </button>

            {/* Spacer - pushes Send button to the right */}
            <div className="flex-1 min-w-0" />

            {/* Send / Stop - Right aligned */}
            {disabled && onStop ? (
              <button
                type="button"
                onClick={onStop}
                className="p-2 max-sm:p-2.5 rounded-md bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors flex-shrink-0"
                title="Stop"
                aria-label="Stop"
              >
                <Square size={18} className="max-sm:w-5 max-sm:h-5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSend}
                disabled={disabled || (!input.trim() && attachments.length === 0)}
                className="p-2 max-sm:p-2.5 rounded-md bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed text-white transition-colors flex-shrink-0"
                title="Send"
                aria-label="Send message"
              >
                <Send size={18} className="max-sm:w-5 max-sm:h-5" />
              </button>
            )}
          </div>

          {/* Bottom row: Selection controls - hidden on mobile (under 768px) */}
          {!isMobile && (
            <div className="flex items-center gap-2 px-3 flex-wrap">
              {/* Agent selector */}
              <div
                className="relative"
                title={isAgentLocked ? 'Agent is locked to this conversation' : undefined}
              >
                <select
                  value={agentDefinitionId ?? ''}
                  onChange={(e) => onAgentChange(e.target.value ? Number(e.target.value) : null)}
                  disabled={isAgentLocked}
                  className={`appearance-none bg-white/5 rounded-md pl-6 pr-5 py-1 text-base max-sm:text-base sm:text-xs text-white/60 transition-colors focus:outline-none ${
                    isAgentLocked
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-white/10 hover:text-white/80 cursor-pointer'
                  }`}
                >
                  {agentDefinitions.map((a) => (
                    <option key={a.id} value={a.id} className="bg-[#1a1a1a] text-white">{a.display_name}</option>
                  ))}
                </select>
                <InfinityIcon size={11} className="absolute left-1.5 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                <ChevronDown size={10} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
              </div>

              {/* Model selector */}
              <div className="relative">
                <select
                  value={model}
                  onChange={(e) => onModelChange(e.target.value)}
                  className="appearance-none bg-white/5 hover:bg-white/10 rounded-md px-2 pr-5 py-1 text-base max-sm:text-base sm:text-xs text-white/60 hover:text-white/80 transition-colors focus:outline-none cursor-pointer"
                >
                  <option value="claude-haiku-4-5-20251001" className="bg-[#1a1a1a] text-white">Haiku</option>
                  <option value="claude-sonnet-4-6" className="bg-[#1a1a1a] text-white">Sonnet 4.6</option>
                  <option value="claude-opus-4-6" className="bg-[#1a1a1a] text-white">Opus 4.6</option>
                </select>
                <ChevronDown size={10} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
              </div>

              {/* Workspace selector */}
              {workspaces.length > 0 && (
                <div className="relative" title={workspaces.find(w => w.id === workspaceId)?.name ?? 'Select workspace'}>
                  <select
                    value={workspaceId ?? ''}
                    onChange={(e) => onWorkspaceChange(e.target.value ? Number(e.target.value) : null)}
                    className="appearance-none bg-white/5 hover:bg-white/10 rounded-md pl-6 pr-5 py-1 text-base max-sm:text-base sm:text-xs text-white/60 hover:text-white/80 transition-colors focus:outline-none cursor-pointer max-w-[120px] truncate"
                  >
                    {workspaces.map((w) => (
                      <option key={w.id} value={w.id} className="bg-[#1a1a1a] text-white">{w.name}</option>
                    ))}
                  </select>
                  <FolderOpen size={11} className="absolute left-1.5 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                  <ChevronDown size={10} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                </div>
              )}
            </div>
          )}
        </div>
        </div>

        {/* Bottom sheet settings panel - mobile only */}
        {isMobile && showSettingsPanel && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setShowSettingsPanel(false)}
            />

            {/* Bottom sheet */}
            <div className="fixed bottom-0 left-0 right-0 bg-zinc-900 border-t border-white/10 rounded-t-2xl z-50 animate-in slide-in-from-bottom duration-200 max-h-[60vh] overflow-y-auto">
              {/* Handle pill */}
              <div className="flex justify-center py-2">
                <div className="w-10 h-1 rounded-full bg-zinc-700" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-4 pb-2 border-b border-white/5">
                <span className="text-sm font-medium text-zinc-300">Chat Settings</span>
                <button
                  onClick={() => setShowSettingsPanel(false)}
                  className="p-2 -mr-2 text-zinc-500 hover:text-zinc-300"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Selectors in vertical stack */}
              <div className="p-4 space-y-3">
                {/* Agent selector */}
                <div>
                  <label className="block text-xs text-white/40 mb-1.5 px-1">
                    Agent {isAgentLocked && <span className="text-white/30">(locked)</span>}
                  </label>
                  <div className="relative">
                    <select
                      value={agentDefinitionId ?? ''}
                      onChange={(e) => onAgentChange(e.target.value ? Number(e.target.value) : null)}
                      disabled={isAgentLocked}
                      className={`w-full appearance-none bg-white/5 rounded-md pl-9 pr-8 py-3 text-base text-white/80 transition-colors focus:outline-none min-h-[48px] ${
                        isAgentLocked
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-white/10 cursor-pointer'
                      }`}
                    >
                      {agentDefinitions.map((a) => (
                        <option key={a.id} value={a.id} className="bg-[#1a1a1a] text-white">{a.display_name}</option>
                      ))}
                    </select>
                    <InfinityIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                  </div>
                </div>

                {/* Model selector */}
                <div>
                  <label className="block text-xs text-white/40 mb-1.5 px-1">Model</label>
                  <div className="relative">
                    <select
                      value={model}
                      onChange={(e) => onModelChange(e.target.value)}
                      className="w-full appearance-none bg-white/5 hover:bg-white/10 rounded-md px-4 pr-8 py-3 text-base text-white/80 transition-colors focus:outline-none cursor-pointer min-h-[48px]"
                    >
                      <option value="claude-haiku-4-5-20251001" className="bg-[#1a1a1a] text-white">Haiku</option>
                      <option value="claude-sonnet-4-6" className="bg-[#1a1a1a] text-white">Sonnet 4.6</option>
                      <option value="claude-opus-4-6" className="bg-[#1a1a1a] text-white">Opus 4.6</option>
                    </select>
                    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                  </div>
                </div>

                {/* Workspace selector */}
                {workspaces.length > 0 && (
                  <div>
                    <label className="block text-xs text-white/40 mb-1.5 px-1">Workspace</label>
                    <div className="relative">
                      <select
                        value={workspaceId ?? ''}
                        onChange={(e) => onWorkspaceChange(e.target.value ? Number(e.target.value) : null)}
                        className="w-full appearance-none bg-white/5 hover:bg-white/10 rounded-md pl-9 pr-8 py-3 text-base text-white/80 transition-colors focus:outline-none cursor-pointer min-h-[48px]"
                      >
                        {workspaces.map((w) => (
                          <option key={w.id} value={w.id} className="bg-[#1a1a1a] text-white">{w.name}</option>
                        ))}
                      </select>
                      <FolderOpen size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                    </div>
                  </div>
                )}
              </div>

              {/* Safe area padding for home indicator */}
              <div className="h-6" />
            </div>
          </>
        )}
      </div>
    )
  }
)

export { MODEL_LABELS }
